import React, { useState, useEffect } from 'react';
import {
    X, Search, Users, UserPlus, UserMinus, Calendar,
    CheckCircle2, Clock, Loader2, AlertCircle, BarChart2
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const EnrollmentManagementModal = ({ isOpen, onClose, course }) => {
    const [activeTab, setActiveTab] = useState('enrolled'); // 'enrolled' or 'add'
    const [enrollments, setEnrollments] = useState([]);
    const [availableEmployees, setAvailableEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [enrollmentDate, setEnrollmentDate] = useState(
        new Date().toISOString().split('T')[0]
    );
    const [enrolling, setEnrolling] = useState(false);

    useEffect(() => {
        if (isOpen && course) {
            fetchEnrollments();
            fetchAvailableEmployees();
        }
    }, [isOpen, course]);

    const fetchEnrollments = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await api.get(`/courses/${course.id}/enrollments`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setEnrollments(response.data.enrollments || []);
        } catch (error) {
            console.error('Error fetching enrollments:', error);
            toast.error('Failed to fetch enrollments');
        } finally {
            setLoading(false);
        }
    };

    const fetchAvailableEmployees = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.get('/employees', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAvailableEmployees(response.data.employees || []);
        } catch (error) {
            console.error('Error fetching employees:', error);
        }
    };

    const handleEnroll = async () => {
        if (selectedEmployees.length === 0) {
            toast.error('Please select at least one employee');
            return;
        }

        setEnrolling(true);
        const loadingToast = toast.loading(`Enrolling ${selectedEmployees.length} employee(s)...`);

        try {
            const token = localStorage.getItem('token');
            let successCount = 0;
            let alreadyEnrolledCount = 0;

            for (const employeeId of selectedEmployees) {
                try {
                    await api.post(`/courses/${course.id}/enrollments`, {
                        employee_id: employeeId,
                        enrollment_date: enrollmentDate
                    }, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    successCount++;
                } catch (error) {
                    if (error.response?.data?.message?.includes('already enrolled')) {
                        alreadyEnrolledCount++;
                    } else {
                        throw error;
                    }
                }
            }

            if (successCount > 0) {
                toast.success(`${successCount} employee(s) enrolled successfully`, { id: loadingToast });
            } else if (alreadyEnrolledCount > 0) {
                toast.error('Selected employees are already enrolled', { id: loadingToast });
            }

            setSelectedEmployees([]);
            fetchEnrollments();
            setActiveTab('enrolled');
        } catch (error) {
            console.error('Error enrolling employees:', error);
            toast.error('Failed to enroll employees', { id: loadingToast });
        } finally {
            setEnrolling(false);
        }
    };

    const handleUnenroll = async (enrollmentId, employeeName) => {
        if (!window.confirm(`Are you sure you want to unenroll ${employeeName} from this course?`)) {
            return;
        }

        const loadingToast = toast.loading('Removing enrollment...');
        try {
            const token = localStorage.getItem('token');
            await api.delete(`/courses/${course.id}/enrollments/${enrollmentId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Employee unenrolled successfully', { id: loadingToast });
            fetchEnrollments();
        } catch (error) {
            console.error('Error unenrolling employee:', error);
            toast.error('Failed to unenroll employee', { id: loadingToast });
        }
    };

    const toggleEmployeeSelection = (employeeId) => {
        setSelectedEmployees(prev =>
            prev.includes(employeeId)
                ? prev.filter(id => id !== employeeId)
                : [...prev, employeeId]
        );
    };

    const selectAllFiltered = () => {
        const filteredIds = filteredAvailableEmployees.map(e => e.id);
        setSelectedEmployees(filteredIds);
    };

    const clearSelection = () => {
        setSelectedEmployees([]);
    };

    // Filter out already enrolled employees from available list
    const enrolledEmployeeIds = enrollments.map(e => e.employee_id);
    const unenrolledEmployees = availableEmployees.filter(
        emp => !enrolledEmployeeIds.includes(emp.id)
    );

    // Apply search filter
    const filteredEnrollments = enrollments.filter(e =>
        `${e.first_name} ${e.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.position?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredAvailableEmployees = unenrolledEmployees.filter(e =>
        `${e.first_name} ${e.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.position?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const getStatusBadge = (status) => {
        switch (status) {
            case 'completed':
                return (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        <CheckCircle2 className="w-3 h-3" /> Completed
                    </span>
                );
            case 'in_progress':
                return (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                        <Clock className="w-3 h-3" /> In Progress
                    </span>
                );
            default:
                return (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                        <Clock className="w-3 h-3" /> Not Started
                    </span>
                );
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-0 md:p-4 transition-all">
            <div className="bg-white w-full h-full md:h-auto md:max-h-[90vh] md:max-w-3xl md:rounded-2xl shadow-2xl overflow-hidden flex flex-col">
                {/* Header */}
                <div className="px-4 py-3 md:px-6 md:py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0 safe-area-top">
                    <div>
                        <h2 className="text-lg md:text-xl font-bold text-gray-800">Manage Enrollments</h2>
                        <p className="text-xs md:text-sm text-gray-600 truncate max-w-[200px] md:max-w-md">{course?.name}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="px-4 pt-3 md:px-6 md:pt-4 flex-shrink-0">
                    <div className="flex gap-4 border-b border-gray-200 overflow-x-auto">
                        <button
                            onClick={() => {
                                setActiveTab('enrolled');
                                setSearchQuery('');
                            }}
                            className={`pb-3 px-1 font-medium text-sm transition-colors border-b-2 whitespace-nowrap ${activeTab === 'enrolled'
                                ? 'border-primary text-primary'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            <span className="flex items-center gap-2">
                                <Users className="w-4 h-4" />
                                Enrolled ({enrollments.length})
                            </span>
                        </button>
                        <button
                            onClick={() => {
                                setActiveTab('add');
                                setSearchQuery('');
                            }}
                            className={`pb-3 px-1 font-medium text-sm transition-colors border-b-2 whitespace-nowrap ${activeTab === 'add'
                                ? 'border-primary text-primary'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            <span className="flex items-center gap-2">
                                <UserPlus className="w-4 h-4" />
                                Add Employees ({unenrolledEmployees.length})
                            </span>
                        </button>
                    </div>
                </div>

                {/* Search */}
                <div className="px-4 py-3 md:px-6 md:py-4 flex-shrink-0">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                            type="text"
                            placeholder="Search employees..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                        />
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto px-4 md:px-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-primary" />
                        </div>
                    ) : activeTab === 'enrolled' ? (
                        /* Enrolled Employees List */
                        filteredEnrollments.length === 0 ? (
                            <div className="text-center py-12">
                                <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                                <h3 className="text-gray-800 font-semibold mb-1">No Enrollments Yet</h3>
                                <p className="text-gray-500 text-sm">
                                    {searchQuery
                                        ? 'No matching employees found'
                                        : 'Add employees to enroll them in this course'}
                                </p>
                                {!searchQuery && (
                                    <button
                                        onClick={() => setActiveTab('add')}
                                        className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-hover text-white rounded-lg font-medium text-sm transition-colors"
                                    >
                                        <UserPlus className="w-4 h-4" />
                                        Add Employees
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-2 pb-4">
                                {filteredEnrollments.map((enrollment) => (
                                    <div
                                        key={enrollment.id}
                                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                                <span className="text-primary font-semibold text-sm">
                                                    {enrollment.first_name?.[0]}{enrollment.last_name?.[0]}
                                                </span>
                                            </div>
                                            <div>
                                                <p className="font-medium text-gray-800 text-sm">
                                                    {enrollment.first_name} {enrollment.last_name}
                                                </p>
                                                <p className="text-xs text-gray-500 truncate max-w-[120px] md:max-w-none">
                                                    {enrollment.position || 'No position'}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 md:gap-3">
                                            {getStatusBadge(enrollment.status)}
                                            <span className="text-xs text-gray-500 hidden md:flex items-center gap-1">
                                                <Calendar className="w-3 h-3" />
                                                {new Date(enrollment.enrollment_date || enrollment.created_at).toLocaleDateString()}
                                            </span>
                                            <button
                                                onClick={() => handleUnenroll(
                                                    enrollment.id,
                                                    `${enrollment.first_name} ${enrollment.last_name}`
                                                )}
                                                className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                title="Unenroll"
                                            >
                                                <UserMinus className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    ) : (
                        /* Add Employees List */
                        <>
                            {/* Selection controls */}
                            {filteredAvailableEmployees.length > 0 && (
                                <div className="flex flex-col md:flex-row md:items-center justify-between mb-3 gap-2">
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={selectAllFiltered}
                                            className="text-xs text-primary hover:underline"
                                        >
                                            Select All
                                        </button>
                                        {selectedEmployees.length > 0 && (
                                            <>
                                                <span className="text-gray-300">|</span>
                                                <button
                                                    onClick={clearSelection}
                                                    className="text-xs text-gray-500 hover:underline"
                                                >
                                                    Clear ({selectedEmployees.length})
                                                </button>
                                            </>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <label className="text-xs text-gray-600">Enrollment Date:</label>
                                        <input
                                            type="date"
                                            value={enrollmentDate}
                                            onChange={(e) => setEnrollmentDate(e.target.value)}
                                            className="text-xs px-2 py-1 border border-gray-300 rounded-lg"
                                        />
                                    </div>
                                </div>
                            )}

                            {filteredAvailableEmployees.length === 0 ? (
                                <div className="text-center py-12">
                                    <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                                    <h3 className="text-gray-800 font-semibold mb-1">No Employees Available</h3>
                                    <p className="text-gray-500 text-sm">
                                        {searchQuery
                                            ? 'No matching employees found'
                                            : 'All employees are already enrolled in this course'}
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-2 pb-4">
                                    {filteredAvailableEmployees.map((employee) => (
                                        <div
                                            key={employee.id}
                                            onClick={() => toggleEmployeeSelection(employee.id)}
                                            className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${selectedEmployees.includes(employee.id)
                                                ? 'bg-primary/10 border-2 border-primary'
                                                : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                                                }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${selectedEmployees.includes(employee.id)
                                                    ? 'bg-primary border-primary'
                                                    : 'border-gray-300'
                                                    }`}>
                                                    {selectedEmployees.includes(employee.id) && (
                                                        <CheckCircle2 className="w-3 h-3 text-white" />
                                                    )}
                                                </div>
                                                <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                                                    <span className="text-gray-600 font-semibold text-sm">
                                                        {employee.first_name?.[0]}{employee.last_name?.[0]}
                                                    </span>
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-800 text-sm">
                                                        {employee.first_name} {employee.last_name}
                                                    </p>
                                                    <p className="text-xs text-gray-500 truncate max-w-[150px] md:max-w-none">
                                                        {employee.email}
                                                    </p>
                                                </div>
                                            </div>
                                            <span className="text-xs text-gray-500 hidden md:block">
                                                {employee.position || 'No position'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="px-4 py-3 md:px-6 md:py-4 border-t border-gray-200 flex items-center justify-between flex-shrink-0 bg-gray-50 safe-area-bottom">
                    <div className="text-xs md:text-sm text-gray-600">
                        {activeTab === 'enrolled'
                            ? `${enrollments.length} enrolled`
                            : `${selectedEmployees.length} selected`
                        }
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg font-medium text-sm transition-colors"
                        >
                            Close
                        </button>
                        {activeTab === 'add' && selectedEmployees.length > 0 && (
                            <button
                                onClick={handleEnroll}
                                disabled={enrolling}
                                className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-hover text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50"
                            >
                                {enrolling ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <UserPlus className="w-4 h-4" />
                                )}
                                Enroll <span className="hidden md:inline">{selectedEmployees.length} Employee(s)</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EnrollmentManagementModal;
